import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ORDERSTATUS } from 'utils/Constants';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';

import ProjectsPageHeader from 'components/shared/PMProjectsPageHeader';
import ProjectTable from 'components/shared/ProjectTable';

import 'pages/AdminClient/order.css';
import { consultantOrderListSelector } from 'store/Consultant/OrderList';
import { fetchConsultantAllOrders } from 'store/Consultant/OrderList/features';
import {
  CONSULTANT_FILTER_ROUTES,
  CONSULTANT_PROJECT_ROUTES,
  storeConsultantProjectDisplayId,
} from 'pages/consultant/projects/constants';
import ProjectSearchBar from 'components/shared/ProjectSearchBar';

const normalizeUrl = (url) => {
  if (!url) return '';
  return url
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');
};

export default function ConsultantValidatedProjects() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { validatedOrders = [], isLoading, isError } = useSelector(consultantOrderListSelector);

  const [searchValue, setSearchValue] = useState('');
  const [selectedWebsiteUrl] = useWebsiteFilter();

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
    dispatch(fetchConsultantAllOrders());
  }, [dispatch]);

  const filteredOrders = useMemo(() => {
    const q = (searchValue || '').toString().trim().toLowerCase();

    const matchesSearch = (order) => {
      if (!q) return true;
      const haystack = [
        order?.title,
        order?.keyword,
        order?.url,
        order?.urlPage,
        order?.websiteUrl,
        order?.website?.urlSite,
      ]
        .filter(Boolean)
        .map((v) => v.toString().toLowerCase())
        .join(' ');
      return haystack.includes(q);
    };

    if (!selectedWebsiteUrl) return validatedOrders.filter(matchesSearch);

    const normalizedSelectedUrl = normalizeUrl(selectedWebsiteUrl);
    return validatedOrders
      .filter((order) => {
        const orderWebsiteUrl = order.websiteUrl || order.website?.urlSite || '';
        return normalizeUrl(orderWebsiteUrl) === normalizedSelectedUrl;
      })
      .filter(matchesSearch);
  }, [validatedOrders, searchValue, selectedWebsiteUrl]);

  return (
    <div>
      <ProjectsPageHeader
        activeFilter={ORDERSTATUS.VALID}
        filterRoutes={CONSULTANT_FILTER_ROUTES}
        hideNonAttributedFilter
        hideAddButton
      />

      <ProjectSearchBar
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />

      <ProjectTable
        orderStatus={ORDERSTATUS.VALID}
        orders={filteredOrders}
        isLoading={isLoading}
        isError={isError}
        fetchDisplayData={storeConsultantProjectDisplayId}
        onRowClickRedirectPath={CONSULTANT_PROJECT_ROUTES.DETAILS}
        hideTitle={true}
        hideSearch={true}
        disableWebsiteFilter={true}
        hidePaginationSelector={true}
        hideEmptyStateCreateProject
      />
    </div>
  );
}
